"use client";

import * as React from "react";

import Image from "next/image";
import Link from "next/link";

import { ROUTES } from "@unreserved/constants/routes";
import { cn } from "@unreserved/utils/cn";
import { Menu } from "lucide-react";

import { ProfileMenuDesktop } from "@/app/_components/my-profile/profile-menu";
import { Button } from "@/app/_components/shared/button";
import { IMAGES } from "@/app/_libs/constants/images";

interface LandingHeaderNavbarProps {
  onToggleMobileMenu: () => void;
  isLoggedIn: boolean;
}

export function LandingHeaderNavbar({ onToggleMobileMenu, isLoggedIn }: LandingHeaderNavbarProps) {
  const [profileUiOpen, setProfileUiOpen] = React.useState(false);

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      className={cn(
        "border-border-100 sticky top-0 z-[60] w-full border-b bg-white transition-colors",
        profileUiOpen && "bg-white/0 backdrop-blur",
      )}>
      <div className="flex items-center justify-between px-4 py-4 sm:px-6 sm:py-5 md:px-8 md:py-6 lg:px-16">
        <div className="flex items-center md:gap-8 lg:gap-12">
          <Button
            variant="ghost"
            type="button"
            className="relative z-[60] inline-flex items-center justify-center rounded-md p-2 text-black md:hidden"
            aria-label="Toggle navigation"
            onClick={(e) => {
              e.stopPropagation();
              onToggleMobileMenu();
            }}>
            <Menu className="h-5 w-5" />
          </Button>

          <Link
            href="/"
            onClick={handleLogoClick}
            className="relative h-[28px] w-[98px] flex-shrink-0 sm:h-[20px] sm:w-[126px] md:h-[28px] md:w-[154px]">
            <Image
              src={IMAGES.LOGO}
              alt="Unreserved Logo"
              fill
              className="h-full w-full"
              sizes="154px"
              priority
              objectFit="contain"
            />
          </Link>

          <nav className="hidden items-center gap-2 md:flex md:gap-4 lg:gap-10">
            <Link
              href="#about"
              className="hover:text-primary-700 scroll-smooth text-sm font-medium text-black transition-colors lg:text-base">
              About Us
            </Link>
            <Link
              href="#features"
              className="hover:text-primary-700 scroll-smooth text-sm font-medium text-black transition-colors lg:text-base">
              Features
            </Link>
            <Link
              href="#models"
              className="hover:text-primary-700 scroll-smooth text-sm font-medium text-black transition-colors lg:text-base">
              Models
            </Link>
            <Link
              href="#download"
              className="hover:text-primary-700 scroll-smooth text-sm font-medium text-black transition-colors lg:text-base">
              Download App
            </Link>
          </nav>
        </div>

        {/* Desktop auth / profile */}
        <div className="hidden items-center gap-3 md:flex">
          {isLoggedIn ? (
            <ProfileMenuDesktop goBackToDashboard onOpenStateChange={setProfileUiOpen} />
          ) : (
            <>
              <Button
                variant="outline"
                className="h-10 border-black text-black hover:bg-black hover:text-white md:px-4 lg:px-5"
                asChild>
                <Link href={ROUTES.AUTH.LOGIN}>Login</Link>
              </Button>
              <Button
                className="bg-primary-700 hover:bg-primary-700/90 h-10 text-white md:px-2 lg:px-5"
                asChild>
                <Link href={ROUTES.AUTH.SIGNUP}>Sign Up</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
